import React from 'react';
import { MegaphoneIcon, PaperPlaneTiltIcon } from '@phosphor-icons/react';
import { UniversalLink } from '@plone/volto/components';

import {
  MPFAlert,
  MPFButton,
  MPFInput,
  MPFModal,
} from 'volto-ministerio-publico-federal/components/UI';
import { useZodForm } from 'volto-ministerio-publico-federal/hooks';

import FeedbackStatusPanel from './FeedbackStatusPanel';
import { DENUNCIAS_PATH, FEEDBACK_COPY, SUBJECT_MAX_LENGTH } from './constants';
import {
  FEEDBACK_INITIAL_VALUES,
  feedbackSchema,
  type FeedbackFormValues,
} from './feedbackSchema';
import { useNotFoundFeedback } from './useNotFoundFeedback';

export interface NotFoundFeedbackModalProps {
  open: boolean;
  onClose: () => void;
  /** Caminho da página que deu 404 — vai junto no envio e aparece no aviso. */
  pagePath: string;
}

type FormAlert = { type: 'warning' | 'error'; message: string };

/**
 * Modal "Não encontrou o que precisava?" (Figma 5376:37918).
 *
 * Três telas dentro do mesmo `MPFModal`: formulário, sucesso e erro. O
 * formulário permanece montado por trás do desfecho de erro, então "Tentar
 * novamente" devolve o usuário ao que ele já tinha digitado.
 */
export const NotFoundFeedbackModal: React.FC<NotFoundFeedbackModalProps> = ({
  open,
  onClose,
  pagePath,
}) => {
  const formId = React.useId();
  const { status, errorMessage, submit, backToForm } =
    useNotFoundFeedback(pagePath);
  const [submitAlert, setSubmitAlert] = React.useState<string>('');

  // O `onSubmit` precisa devolver erros para dentro do formulário, mas o
  // formulário só nasce depois — daí a ref: ela é preenchida por efeito e
  // lida apenas no envio, quando já aponta para o `setFieldError` corrente.
  const setFieldErrorRef = React.useRef<
    (field: keyof FeedbackFormValues, message: string) => void
  >(() => {});

  const handleSubmit = React.useCallback(
    async (values: FeedbackFormValues) => {
      setSubmitAlert('');
      const result = await submit(values);
      if (result.fieldErrors) {
        (
          Object.entries(result.fieldErrors) as Array<
            [keyof FeedbackFormValues, string]
          >
        ).forEach(([field, message]) =>
          setFieldErrorRef.current(field, message),
        );
      }
      if (result.alert) setSubmitAlert(result.alert);
    },
    [submit],
  );

  const form = useZodForm<FeedbackFormValues>({
    schema: feedbackSchema,
    initialValues: FEEDBACK_INITIAL_VALUES,
    onSubmit: handleSubmit,
  });

  React.useEffect(() => {
    setFieldErrorRef.current = form.setFieldError;
  }, [form.setFieldError]);

  const { values, errors, hasErrors, isSubmitting, submitCount } = form;

  // Um aviso por vez, e só quando há o que avisar: falha de envio manda mais
  // que "revise os campos", e nenhum dos dois aparece enquanto o formulário
  // está intocado. A mensagem informativa (nota LGPD) é fixa e mora no fim
  // do modal, onde o design a colocou.
  const alert: FormAlert | null = React.useMemo(() => {
    if (submitAlert) {
      return {
        type: submitAlert === FEEDBACK_COPY.invalidForm ? 'warning' : 'error',
        message: submitAlert,
      };
    }
    if (submitCount > 0 && hasErrors) {
      return { type: 'warning', message: FEEDBACK_COPY.invalidForm };
    }
    return null;
  }, [hasErrors, submitAlert, submitCount]);

  const handleClose = React.useCallback(() => {
    setSubmitAlert('');
    onClose();
  }, [onClose]);

  const handleRetry = React.useCallback(() => {
    setSubmitAlert('');
    backToForm();
  }, [backToForm]);

  const isForm = status === 'idle';

  return (
    <MPFModal
      open={open}
      onClose={handleClose}
      title={FEEDBACK_COPY.title}
      size="md"
      dismissible={!isSubmitting}
      footer={
        isForm ? (
          <div className="flex flex-col-reverse gap-3 sm:flex-row sm:items-center sm:justify-between">
            <UniversalLink
              href={DENUNCIAS_PATH}
              className="mpf-feedback__denuncia"
            >
              <MegaphoneIcon size={24} aria-hidden="true" />
              <span>
                {FEEDBACK_COPY.denunciaPrompt}{' '}
                <strong className="underline">
                  {FEEDBACK_COPY.denunciaLink}
                </strong>
              </span>
            </UniversalLink>
            <MPFButton
              type="submit"
              form={formId}
              variant="primary"
              size="m"
              loading={isSubmitting}
            >
              <PaperPlaneTiltIcon size={24} aria-hidden="true" />
              {FEEDBACK_COPY.submit}
            </MPFButton>
          </div>
        ) : undefined
      }
    >
      {status === 'success' && (
        <FeedbackStatusPanel
          variant="success"
          title={FEEDBACK_COPY.successTitle}
          description={FEEDBACK_COPY.successBody}
          onClose={handleClose}
        />
      )}

      {status === 'error' && (
        <FeedbackStatusPanel
          variant="error"
          title={FEEDBACK_COPY.errorTitle}
          description={errorMessage}
          onClose={handleClose}
          onRetry={handleRetry}
        />
      )}

      {isForm && (
        <>
          <p className="mb-0 text-base text-on-surface">
            {FEEDBACK_COPY.intro}
          </p>

          {alert && <MPFAlert type={alert.type} message={alert.message} />}

          <form
            id={formId}
            noValidate
            className="flex flex-col gap-4"
            onSubmit={form.handleSubmit}
          >
            {/* O preflight do Tailwind está desligado (tailwind.config.js),
                então o `fieldset` ainda carrega a borda e o padding do
                navegador — daí o reset explícito. */}
            <fieldset
              disabled={isSubmitting}
              className="m-0 flex flex-col gap-4 border-0 p-0"
            >
              <legend className="sr-only">{FEEDBACK_COPY.title}</legend>

              <MPFInput
                label="Seu nome"
                required
                autoComplete="name"
                placeholder="Digite aqui seu nome"
                errorText={errors.name}
                {...form.getFieldProps('name')}
              />

              <MPFInput
                type="email"
                label="E-mail"
                required
                autoComplete="email"
                placeholder="Digite aqui seu melhor email (ex: email@email.com)"
                errorText={errors.email}
                {...form.getFieldProps('email')}
              />

              <MPFInput
                label="O que você estava procurando?"
                required
                maxLength={SUBJECT_MAX_LENGTH}
                placeholder="Ex.: portaria, processo, contato de uma unidade..."
                errorText={errors.subject}
                helperText={`${values.subject.length}/${SUBJECT_MAX_LENGTH} caracteres`}
                {...form.getFieldProps('subject')}
              />

              <MPFInput
                type="url"
                inputMode="url"
                label="Você tem o link que tentou acessar?"
                placeholder="https://"
                errorText={errors.sourceUrl}
                helperText="Caso tenha acesso ao link que está tentando acessar, cole aqui para nos ajudar a localizá-lo."
                {...form.getFieldProps('sourceUrl')}
              />
            </fieldset>
          </form>

          <MPFAlert type="info" message={FEEDBACK_COPY.lgpd(pagePath)} />
        </>
      )}
    </MPFModal>
  );
};

export default NotFoundFeedbackModal;
